<template>
  <div v-if="clientModel.isAuthenticated && clientModel.enableShoppingListExtended">
    <oie-base-action
      v-tooltip.top="{ content: tStatic('addToShoppingListTooltip') }"
      size="large"
      @click.stop="initialize">
      <oie-base-icon icon="shopping-list" size="s"></oie-base-icon>
    </oie-base-action>
    <oie-portal-wrapper :name="name">
      <template #title>
        <h2>{{ tStatic('addToShoppingList') }}</h2>
      </template>
      <div v-if="initialized" class="add-to-shopping-list-slide-in__content">
        <oie-message-bar
          v-if="hasGlobalSelection">
          {{ machineMessage }}
        </oie-message-bar>
        <template v-if="mandatoryComment">
          <p>{{ tStatic('mandatoryCommentExplanation') }}</p>
          <oie-mandatory-comments v-model="mandatoryComment"></oie-mandatory-comments>
        </template>
        <template v-if="shoppingLists && shoppingLists.length">
          <p>{{ tStatic('selectShoppingList') }}</p>
          <ul class="add-to-shopping-list-slide-in__lists">
            <li v-for="list in shoppingLists" :key="list.id">
              <button class="body-smaller-semi-bold" :disabled="!canAddItem" @click="selectList(list.id)">
                {{ list.name }}
              </button>
            </li>
          </ul>
        </template>
        <oie-base-divider></oie-base-divider>
        <validation-observer
          ref="observer"
          tag="form"
          class="inputs-container"
          @submit.prevent="addToNewList">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            slim
            mode="passive">
            <oie-base-input
              v-model="newListName"
              :disabled="!canAddItem"
              :label-text="tStatic('saveToNewList')"
              :error-messages="errors"></oie-base-input>
          </validation-provider>
          <oie-base-action type="submit" theme="primary" :disabled="!canAddItem">
            {{ tStatic('saveToNewListButtonLabel') }}
          </oie-base-action>
        </validation-observer>
      </div>
      <p v-else>
        {{ tStatic('loading') }}
      </p>
    </oie-portal-wrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import httpService from '@/core/http-service'

export default {
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    initialized: false,
    mandatoryComment: null,
    shoppingLists: null,
    newListName: ''
  }),
  computed: {
    ...mapGetters('myMachines', ['globalMachine', 'globalMachineOwner', 'machineDictionary', 'machineOwnerDictionary', 'hasGlobalSelection', 'readableGlobalSelection']),
    name() {
      return _.uniqueId('add-to-shopping-list-slide-in-')
    },
    itemId() {
      return this.product.items[0].id
    },
    canAddItem() {
      return !this.mandatoryComment || _.chain(this.mandatoryComment).flatMap(val => val).every(comment => comment.value).value()
    },
    machineMessage() {
      return this.hasGlobalSelection ? this.tStatic('addItemToShoppingListWithGlobalSelection').replace('{0}', this.readableGlobalSelection) : ''
    }
  },
  methods: {
    ...mapActions('ui', ['setActiveSlideIn', 'addNotification']),
    async initialize() {
      this.setActiveSlideIn(this.name)
      await this.checkIfMandatoryComments()
      await this.getShoppingLists()
      this.initialized = true
    },
    reset() {
      this.mandatoryComment = null
      this.newListName = ''
      this.shoppingLists = null
      this.setActiveSlideIn(null)
      this.initialized = false
    },
    async checkIfMandatoryComments() {
      const { data } = await httpService.get('shoppinglist/checkRow', { itemId: this.itemId, productId: this.product.id, quantity: 1 })
      if(data.mandatoryComment) {
        this.mandatoryComment = data.mandatoryComment
      }
    },
    async getShoppingLists() {
      const { data } = await httpService.get('shoppinglist/loadAll')
      this.shoppingLists = data.shoppingLists
      this.createdByList = data.createdByList
    },
    async selectList(listId) {
      const selectedList = _.find(this.shoppingLists, list => list.id === listId)
      if(!selectedList) return

      await httpService.post('shoppinglist/addRow', { id: selectedList.id,
        rows: [{
          itemId: this.itemId,
          productId: this.product.id,
          quantity: 1,
          machineId: this.globalMachine,
          machineOwnerId: this.globalMachineOwner,
          mandatoryComment: this.mandatoryComment ? this.mandatoryComment[this.itemId] : null
        }] })

      this.reset()
    },
    async addToNewList() {
      const valid = await this.$refs.observer.validate()
      if(!valid) return
      await httpService.post('shoppinglist/addRow', { name: this.newListName,
        rows: [{
          itemId: this.itemId,
          productId: this.product.id,
          quantity: 1,
          machineId: this.globalMachine,
          machineOwnerId: this.globalMachineOwner,
          mandatoryComment: this.mandatoryComment ? this.mandatoryComment[this.itemId] : null
        }] })

      this.reset()
    }
  }
}
</script>

<style lang="scss">
.add-to-shopping-list-slide-in__content {
  display: flex;
  flex-direction: column;
  gap: measure(half);
}

.add-to-shopping-list-slide-in__lists {
  @include reset-list;
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
}

.add-to-shopping-list-slide-in__lists button {
  padding: measure(half) measure(quarter);
  word-wrap: break-word;
  background-color: color(background-gray-5);
  border: 1px solid color(border-gray);
  width: 100%;
  text-align: left;
  cursor: pointer;
}
</style>
